import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import { ModalRef, UiModule } from '@freelancer/ui';
import { ButtonColor } from '@freelancer/ui/button';

@Component({
  standalone: true,
  imports: [UiModule],
  selector: 'app-pstn-desktop-call-confirm-modal',
  template: `
    <div
      class="PstnDesktopCallConfirmModal"
      flTrackingSection="PstnDesktopCallConfirmModal"
    >
      <h2 class="PstnDesktopCallConfirmModalTitle text-body-24 mb-xsmall">
        {{ heading }}
      </h2>

      @if (callerPhone) {
        <p
          class="mb-small"
          i18n="PSTN desktop confirm security phone explanation"
        >
          We'll call
          <span class="font-bold">{{ callerPhone }}</span
          >. Answer when your phone rings to get connected.
        </p>
      } @else {
        <p
          class="mb-small"
          i18n="PSTN desktop no security phone explanation"
        >
          Add a security phone number to your account to start phone calls.
        </p>
      }

      @if (callerPhone) {
        <div class="PstnDesktopCallConfirmModalFooter">
          <fl-link
            i18n="PSTN not your number link"
            flTrackingLabel="PstnDesktopCallConfirmModal.NotYourNumber"
            [isPassive]="true"
            [link]="'/user/settings/profile'"
            (click)="handleNotYourNumberClick()"
          >
            Not your number?
          </fl-link>
          <div class="PstnDesktopCallConfirmModalActions">
            <fl-button
              i18n="PSTN desktop confirm cancel"
              flTrackingLabel="PstnDesktopCallConfirmModal.Cancel"
              [color]="ButtonColor.TEXT_DARK"
              (click)="handleCancel()"
            >
              Cancel
            </fl-button>
            <fl-button
              i18n="PSTN desktop confirm primary action"
              flTrackingLabel="PstnDesktopCallConfirmModal.Confirm"
              [color]="ButtonColor.PRIMARY"
              (click)="handleConfirm()"
            >
              Start call
            </fl-button>
          </div>
        </div>
      } @else {
        <div
          class="PstnDesktopCallConfirmModalFooter PstnDesktopCallConfirmModalFooterEndOnly"
        >
          <div class="PstnDesktopCallConfirmModalActions">
            <fl-button
              i18n="PSTN desktop confirm cancel"
              flTrackingLabel="PstnDesktopCallConfirmModal.Cancel"
              [color]="ButtonColor.TEXT_DARK"
              (click)="handleCancel()"
            >
              Cancel
            </fl-button>
            <fl-button
              i18n="PSTN desktop set up phone number action"
              flTrackingLabel="PstnDesktopCallConfirmModal.SetUpPhoneNumber"
              [color]="ButtonColor.PRIMARY"
              (click)="handleNotYourNumberClick()"
            >
              Set up phone number
            </fl-button>
          </div>
        </div>
      }
    </div>
  `,
  styleUrls: ['./pstn-desktop-call-confirm-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PstnDesktopCallConfirmModalComponent implements OnInit {
  private modalRef =
    inject<ModalRef<PstnDesktopCallConfirmModalComponent>>(ModalRef);
  private router = inject(Router);

  @Input() callerPhone = '';
  @Input() heading = '';

  ButtonColor = ButtonColor;

  ngOnInit(): void {
    if (!this.heading) {
      this.heading = this.callerPhone
        ? $localize`:PSTN desktop modal default title:Start phone call`
        : $localize`:PSTN desktop modal no phone title:Set up your phone number`;
    }
  }

  handleCancel(): void {
    this.modalRef.close({ confirmed: false });
  }

  async handleNotYourNumberClick(): Promise<void> {
    if (this.router.url.split('?')[0] === '/user/settings/profile') {
      this.modalRef.close({ confirmed: false });
      return;
    }
    this.modalRef.close({ confirmed: false }, { fromRouteChange: true });
    await this.router.navigate(['/user/settings/profile']);
  }

  handleConfirm(): void {
    this.modalRef.close({ confirmed: true });
  }
}
